/**
 * A spoken scope of work, drafted by AI and finished by a person.
 *
 * Jonathon, 2026-08-24, after watching Samuel build estimates: the rep should
 * be able to *say* what the job is and have AI write a first draft of the
 * scope for him to edit — and then, narrowing it: "the ai feature i simply
 * want on the note section of each line item." So the feature lives on the
 * line item's **note** — the scope of work the customer reads under the
 * heading — and nowhere else. The rep dictates (or types) what the work is,
 * the `scope-draft` Edge Function asks the AI for the scope text, this module
 * decides what of that answer is allowed into the box, and the rep edits it
 * there like any other note. Pricing, quantities and everything else stay the
 * normal process; the AI writes words, only words, for one line at a time.
 *
 * This lives in core rather than in the component (CLAUDE.md rule 5): the
 * model's answer is data, not instructions, and the reduction of that answer
 * to "text that may sit in a note" is a business rule.
 */

/**
 * The bounds a draft lives inside.
 *
 * Transcribed into `supabase/functions/scope-draft/request.ts`, which cannot
 * import the workspace; `supabase/test/scope-draft-match.test.ts` binds the
 * two so they cannot drift. `note` matches what the line editor itself
 * accepts — a scope, not a document.
 */
export const SCOPE_DRAFT_LIMITS = {
  /** Characters of transcript worth sending at all. */
  transcriptMin: 10,
  /** A dictation, not a document. */
  transcriptMax: 8000,
  note: 4000,
} as const;

/** Why this transcript cannot be drafted from, if it cannot. */
export function scopeDraftBlocker(transcript: string): string | null {
  const trimmed = transcript.trim();
  if (trimmed.length < SCOPE_DRAFT_LIMITS.transcriptMin) {
    return "Say (or type) a sentence or two about the work first.";
  }
  if (trimmed.length > SCOPE_DRAFT_LIMITS.transcriptMax) {
    return "That is too long for one draft. Trim it to the work being quoted.";
  }
  return null;
}

/**
 * What the model answered, reduced to what is allowed in a note.
 *
 * The prompt asks for the scope text and nothing else, and a model that
 * wraps it in a code fence anyway has still answered the question — the
 * fence goes, the words stay. Length is capped at what the note field
 * accepts. Returns null when there is nothing usable, which the caller turns
 * into "try again" rather than into an empty box.
 *
 * **Overflow is cut at a line break, not at a character.** The prompt asks
 * for a full scope of work now rather than a handful of bullets (2026-09-06),
 * so an answer that runs long is a real possibility rather than a
 * hypothetical, and it runs long as a list of steps. A hard slice at
 * character 4000 leaves the customer reading half a step — "- Install new
 * flashing around chimne" — which is worse than not having that step at all,
 * because a rep skimming a long draft sees a finished-looking list and misses
 * the ragged end. Cutting back to the last complete line loses the same step
 * and says nothing untrue.
 */
export function parseScopeNote(raw: string): string | null {
  const text = raw
    .trim()
    .replace(/^```[a-z]*\s*/i, "")
    .replace(/```\s*$/, "")
    .trim();

  if (text.length <= SCOPE_DRAFT_LIMITS.note) return text || null;

  const cut = text.slice(0, SCOPE_DRAFT_LIMITS.note);
  const lastBreak = cut.lastIndexOf("\n");
  // A single line longer than the whole note has no break to fall back to;
  // the hard cut is all there is, and it is still better than nothing.
  const kept = (lastBreak > 0 ? cut.slice(0, lastBreak) : cut).trim();
  return kept || null;
}
